import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaWallet,
  FaRupeeSign,
  FaArrowLeft,
  FaUniversity,
  FaCreditCard,
  FaPaypal,
  FaInfoCircle,
  FaCheckCircle,
  FaExclamationTriangle
} from 'react-icons/fa';
import DriverLayout from '../../layouts/DriverLayout';
import {
  getDriverWallet,
  requestWithdrawal,
  reset as resetWallet
} from '../../features/wallet/walletSlice';

const WalletWithdraw = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { driver: driverAuth } = useSelector((state) => state.driverAuth);
  const { wallet, isLoading, isError, isSuccess, message } = useSelector((state) => state.wallet);

  const [withdrawalData, setWithdrawalData] = useState({
    amount: '',
    paymentMethod: 'bank_transfer',
    accountDetails: {
      accountNumber: '',
      ifscCode: '',
      accountHolderName: '',
      bankName: ''
    }
  });

  const [errors, setErrors] = useState({});

  // Get driver ID
  const driverId = driverAuth?._id || driverAuth?.driver?._id;

  useEffect(() => {
    if (driverId && !wallet) {
      dispatch(getDriverWallet(driverId));
    }
  }, [dispatch, driverId, wallet]);

  useEffect(() => {
    if (isError && message) {
      toast.error(message);
      dispatch(resetWallet());
    }

    if (isSuccess && message) {
      toast.success(message);
      dispatch(resetWallet());
      // Redirect to wallet history or dashboard after successful withdrawal
      navigate('/driver/wallet/history');
    }
  }, [isError, isSuccess, message, dispatch, navigate]);

  const validateForm = () => {
    const newErrors = {};

    // Amount validation
    const amount = parseFloat(withdrawalData.amount);
    if (!withdrawalData.amount) {
      newErrors.amount = 'Amount is required';
    } else if (amount < 100) {
      newErrors.amount = 'Minimum withdrawal amount is ₹100';
    } else if (amount > (wallet?.balance || 0)) {
      newErrors.amount = 'Amount exceeds available balance';
    }

    // Account details validation for bank transfer
    if (withdrawalData.paymentMethod === 'bank_transfer') {
      if (!withdrawalData.accountDetails.accountNumber) {
        newErrors.accountNumber = 'Account number is required';
      }
      if (!withdrawalData.accountDetails.ifscCode) {
        newErrors.ifscCode = 'IFSC code is required';
      }
      if (!withdrawalData.accountDetails.accountHolderName) {
        newErrors.accountHolderName = 'Account holder name is required';
      }
      if (!withdrawalData.accountDetails.bankName) {
        newErrors.bankName = 'Bank name is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!driverId) {
      toast.error('Driver ID not found. Please login again.');
      return;
    }

    dispatch(requestWithdrawal({
      driverId,
      ...withdrawalData,
      amount: parseFloat(withdrawalData.amount)
    }));
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setWithdrawalData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setWithdrawalData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[field.split('.')[1] || field]) {
      setErrors(prev => ({
        ...prev,
        [field.split('.')[1] || field]: ''
      }));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (isLoading) {
    return (
      <DriverLayout>
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
          <div className="ml-4 text-gray-700 font-medium">Loading wallet...</div>
        </div>
      </DriverLayout>
    );
  }

  const availableBalance = wallet?.balance || 0;
  const minimumWithdrawal = 100;
  const isPendingWithdrawal = wallet?.pendingWithdrawal?.amount > 0;

  return (
    <DriverLayout>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/driver/dashboard')}
            className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <FaArrowLeft className="text-gray-600" />
          </button>
          <div className="flex items-center">
            <FaWallet className="text-primary text-2xl mr-3" />
            <h1 className="text-2xl font-bold text-gray-800">Withdraw Funds</h1>
          </div>
        </div>

        {/* Balance Card */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 text-white mb-6 shadow-lg">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-blue-100 text-sm">Available Balance</p>
              <p className="text-3xl font-bold">{formatCurrency(availableBalance)}</p>
              <p className="text-blue-100 text-sm mt-1">
                Minimum withdrawal: {formatCurrency(minimumWithdrawal)}
              </p>
            </div>
            <div className="bg-blue-400 p-4 rounded-full">
              <FaRupeeSign className="text-2xl" />
            </div>
          </div>
        </div>

        {/* Pending Withdrawal Alert */}
        {isPendingWithdrawal && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <FaExclamationTriangle className="text-yellow-500 mr-3" />
              <div>
                <p className="font-medium text-yellow-800">Pending Withdrawal</p>
                <p className="text-sm text-yellow-700">
                  You have a pending withdrawal of {formatCurrency(wallet.pendingWithdrawal.amount)}. 
                  Please wait for it to be processed before requesting another withdrawal.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Withdrawal Form */}
        {!isPendingWithdrawal && availableBalance >= minimumWithdrawal ? (
          <div className="bg-white rounded-xl shadow-md p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Amount Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Withdrawal Amount
                </label>
                <div className="relative">
                  <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="number"
                    value={withdrawalData.amount}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                      errors.amount ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter amount"
                    min={minimumWithdrawal}
                    max={availableBalance}
                  />
                </div>
                {errors.amount && (
                  <p className="text-red-500 text-sm mt-1">{errors.amount}</p>
                )}
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Method
                </label>
                <div className="space-y-3">
                  <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      value="bank_transfer"
                      checked={withdrawalData.paymentMethod === 'bank_transfer'}
                      onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                      className="mr-3"
                    />
                    <FaUniversity className="text-primary mr-3" />
                    <div>
                      <p className="font-medium">Bank Transfer</p>
                      <p className="text-sm text-gray-500">Direct transfer to your bank account</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Bank Details */}
              {withdrawalData.paymentMethod === 'bank_transfer' && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium text-gray-800">Bank Account Details</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Holder Name
                    </label>
                    <input
                      type="text"
                      value={withdrawalData.accountDetails.accountHolderName}
                      onChange={(e) => handleInputChange('accountDetails.accountHolderName', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                        errors.accountHolderName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Full name as per bank records"
                    />
                    {errors.accountHolderName && (
                      <p className="text-red-500 text-sm mt-1">{errors.accountHolderName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Number
                    </label>
                    <input
                      type="text"
                      value={withdrawalData.accountDetails.accountNumber}
                      onChange={(e) => handleInputChange('accountDetails.accountNumber', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                        errors.accountNumber ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Bank account number"
                    />
                    {errors.accountNumber && (
                      <p className="text-red-500 text-sm mt-1">{errors.accountNumber}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      IFSC Code
                    </label>
                    <input
                      type="text"
                      value={withdrawalData.accountDetails.ifscCode}
                      onChange={(e) => handleInputChange('accountDetails.ifscCode', e.target.value.toUpperCase())}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                        errors.ifscCode ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="IFSC Code (e.g., SBIN0001234)"
                    />
                    {errors.ifscCode && (
                      <p className="text-red-500 text-sm mt-1">{errors.ifscCode}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bank Name
                    </label>
                    <input
                      type="text"
                      value={withdrawalData.accountDetails.bankName}
                      onChange={(e) => handleInputChange('accountDetails.bankName', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                        errors.bankName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Bank name"
                    />
                    {errors.bankName && (
                      <p className="text-red-500 text-sm mt-1">{errors.bankName}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Info Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <FaInfoCircle className="text-blue-500 mr-3 mt-1" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium mb-1">Withdrawal Information</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Withdrawals are processed within 24-48 hours</li>
                      <li>Minimum withdrawal amount is {formatCurrency(minimumWithdrawal)}</li>
                      <li>Bank transfer fees may apply as per bank policies</li>
                      <li>Ensure bank details are correct to avoid delays</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary hover:bg-primary-dark text-white py-3 px-6 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <FaCheckCircle className="mr-2" />
                    Request Withdrawal
                  </>
                )}
              </button>
            </form>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md p-6 text-center">
            <FaExclamationTriangle className="text-yellow-500 text-4xl mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {isPendingWithdrawal ? 'Withdrawal Already Pending' : 'Insufficient Balance'}
            </h3>
            <p className="text-gray-600 mb-4">
              {isPendingWithdrawal 
                ? 'You have a pending withdrawal request. Please wait for it to be processed.'
                : `You need at least ${formatCurrency(minimumWithdrawal)} to make a withdrawal.`
              }
            </p>
            <button
              onClick={() => navigate('/driver/dashboard')}
              className="bg-primary hover:bg-primary-dark text-white py-2 px-6 rounded-lg font-medium transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        )}
      </div>
    </DriverLayout>
  );
};

export default WalletWithdraw;
